import { useState, useEffect, useMemo, useCallback } from "react";
import ListLeavesInDayService from "../../services/ListLeavesInDayService";
import cx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import Divider from '@material-ui/core/Divider';
import { useFadedShadowStyles } from '@mui-treasury/styles/shadow/faded';
import { Grid } from '@material-ui/core';
import { Alert, FormControl, MenuItem, Select, CircularProgress } from "@mui/material";
import UserService from "../../services/UserService";
import PlanningMissionService from "../../services/PlanningMissionService";
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';

const useStyles = makeStyles(({ palette }) => ({
  card: {
    borderRadius: 12,
    minWidth: 150,
    textAlign: 'center',
    margin: '8px', 
    height: '100%', 
  },
  avatar: {
    width: 60,
    height: 60,
    margin: 'auto'
  },
  heading: {
    fontSize: 18,
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    marginTop: 8,
    marginBottom: 0,
  },
  subheader: {
    fontSize: 14,
    color: palette.grey[500],
    marginBottom: '0.875em',
  },
  statLabel: {
    fontSize: 12,
    color: palette.grey[500],
    fontWeight: 500,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    margin: 0,
    textDecoration: 'underline',
  },
  statValue: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
    letterSpacing: '1px',
  },
  gridLeft: {
    borderRight: "grey 1px solid"
  },
  containerGrid: {
    padding: "10px"
  },
  root: {
    flexGrow: 1,
    padding: '16px' 
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px'
  },
}));

function MissionInDay(props) {
  const [missionInDay, setMissionInDay] = useState([]);
  const styles = useStyles();
  const shadowStyles = useFadedShadowStyles();
  const [direction, setDirection] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [userDirection, setUserDirection] = useState(null);

  const directionOptions = useMemo(() => {
    // Si l'utilisateur n'est pas DG (id !== 1), ne montrer que sa direction
    if (userDirection && parseInt(userDirection, 10) !== 1) {
      return direction.filter(dir => parseInt(dir.direction_id, 10) === parseInt(userDirection, 10));
    }
    // Sinon, montrer toutes les directions
    return direction;
  }, [direction, userDirection]);

  // Chargement initial des données
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsInitialLoading(true);
      const userData = localStorage.getItem('USER');
      
      if (userData) {
        try {
          // Récupérer les infos utilisateur
          const userResult = await UserService.getUserSummaryByUserId(userData);
          
          if (userResult && userResult.data) {
            const dirpers = userResult.data.direction_id;
            setUserDirection(dirpers);
            
            // Récupérer toutes les directions
            const directionResult = await PlanningMissionService.getDirection();
            if (directionResult && directionResult.data) {
              setDirection(directionResult.data);
            }
            
            // Définir la direction sélectionnée selon l'utilisateur
            const selectedDir = dirpers.toString();
            setSelectedDirection(selectedDir);
            
            // Charger les missions pour la direction de l'utilisateur
            const missionResult = await ListLeavesInDayService.getMissioninday(selectedDir);
            if (missionResult && missionResult.data) {
              setMissionInDay(missionResult.data);
            } else {
              setMissionInDay([]);
            }
          }
        } catch (error) {
          console.error('Error fetching initial data:', error);
          setMissionInDay([]);
        } finally {
          setIsInitialLoading(false);
        }
      } else {
        setIsInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleDirectionChange = (event) => {
    const newDirection = event.target.value;
    setSelectedDirection(newDirection);
  };

  const handleFilterData = useCallback(async (directionValue) => {
    if (!directionValue) {
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await ListLeavesInDayService.getMissioninday(directionValue);
      if (result && result.data) {
        setMissionInDay(result.data);
      } else {
        setMissionInDay([]);
      }
    } catch (error) {
      console.error('Error filtering missions:', error);
      setMissionInDay([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleFilterClick = () => {
    if (selectedDirection) {
      handleFilterData(selectedDirection);
    }
  };

  if (isInitialLoading) {
    return (
      <div style={{ flexGrow: 1, padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress color="primary" />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Grid container item xs={12} spacing={2} alignItems="center">
        <Grid item xs={4}>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              labelId="direction-select-label"
              id="direction-select"
              name="direction_id"
              value={selectedDirection || ''}
              onChange={handleDirectionChange}
              displayEmpty
            >
              {directionOptions.map((row) => (
                <MenuItem key={row.direction_id} value={row.direction_id.toString()}>
                  {row.name} ({row.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={1} />

        <Grid item xs={2}>
          <LoadingButton
            fullWidth
            variant="contained"
            color="primary"
            loading={isLoading}
            loadingPosition="start"
            startIcon={<SearchIcon />}
            onClick={handleFilterClick}
            disabled={!selectedDirection || selectedDirection === ''}
          >
            {isLoading ? 'Chargement...' : 'Filtrer'}
          </LoadingButton>
        </Grid>

        <Grid item xs={5} />
      </Grid>

      <Grid container spacing={3} style={{ marginTop: '16px' }}>
        {isLoading ? (
          <Grid item xs={12} className={styles.loadingContainer}>
            <CircularProgress />
          </Grid>
        ) : missionInDay?.length > 0 ? (
          missionInDay.map((mission, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
              <Card className={cx(styles.card, shadowStyles.root)}>
                <CardContent>
                  <Avatar className={styles.avatar} src={mission.name_image} />
                  <h3 className={styles.heading}>{mission.first_name} {mission.last_name}</h3>
                  <span className={styles.subheader}>{mission.function_code}/{mission.direction_code}</span>
                </CardContent>
                <Divider light />
                <Grid container item xs={12} className={styles.containerGrid}>
                  <p className={styles.statValue}>{mission.mission_title?.toUpperCase() || ''}</p>
                </Grid>
                <Grid container justifyContent="center">
                  <Grid container item xs={12} className={styles.containerGrid}>
                    <Grid item xs={6} className={styles.gridLeft}>
                      <p className={styles.statLabel}>Date de début</p>
                      <p className={styles.statValue}>{mission.date_begin}</p>
                    </Grid>
                    <Grid item xs={6}>
                      <p className={styles.statLabel}>Date fin</p>
                      <p className={styles.statValue}>{mission.date_end}</p>
                    </Grid>
                  </Grid>
                  <Divider light /> 
                  <Grid container item xs={12} className={styles.containerGrid}>
                    <Grid item xs={6}>
                      <p className={styles.statLabel}>Direction de destination :</p>
                      <p className={styles.statValue}>{mission.code}</p>
                    </Grid>
                    <Grid item xs={6}>
                      <p className={styles.statLabel}>Lieu de destination :</p>
                      <p className={styles.statValue}>{mission.lieu}</p>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Alert severity="info">Aucun personnel n'est en mission en ce jour</Alert>
          </Grid>
        )}
      </Grid>
    </div>
  );
}

export default MissionInDay;